import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertBanner, Button, DataTable, StatusBadge, useLocale, useToast, type DataTableColumn } from '@ministry/ui/internal';

import { listTenants, type Tenant } from '../../lib/admin-platform.js';
import { tenantStatusEntry } from '../../lib/admin-lifecycle.js';
import { formatInstantDisplay } from '../../lib/dates.js';
import { ApiError } from '../../lib/api-client.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { ForbiddenState } from '../../components/ForbiddenState.js';
import { OnboardTenantModal, EditTenantModal } from './TenantModals.js';

/**
 * SCREEN_SPECS.md I21 — `super_admin` at `platform` scope only. `GET /platform/tenants`
 * has no pagination in the contract (a platform's ministry count is small by nature),
 * so this table renders the full list directly rather than inventing client-side paging
 * over data the server never paginates.
 */
export function TenantsPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState<Tenant | null>(null);
  const [mfaTicket, setMfaTicket] = React.useState<{ token: string; expiresAt: string; username: string } | null>(null);

  const query = useQuery({
    queryKey: ['admin-platform-tenants'],
    queryFn: ({ signal }) => listTenants(signal),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin-platform-tenants'] });

  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('admin.tenant.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const rows = query.data ?? [];

  const columns: DataTableColumn<Tenant>[] = [
    {
      key: 'nameEn',
      header: t('admin.tenant.column.name'),
      visibleFrom: 'always',
      cardSlot: 'title',
      grow: true,
      render: (row) => (locale === 'ne' ? row.nameNe : row.nameEn),
    },
    {
      key: 'codePrefix',
      header: t('admin.tenant.column.prefix'),
      visibleFrom: 'md',
      cardSlot: 'line2',
      render: (row) => row.codePrefix,
    },
    {
      key: 'onboardedAt',
      header: t('admin.tenant.column.onboardedAt'),
      visibleFrom: 'lg',
      cardSlot: 'line3',
      render: (row) => (row.onboardedAt ? formatInstantDisplay(row.onboardedAt, locale) : '—'),
    },
    {
      key: 'status',
      header: t('admin.tenant.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={tenantStatusEntry(row.status)} />,
    },
    {
      key: 'actions',
      header: t('admin.tenant.column.actions'),
      visibleFrom: 'always',
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => setEditTarget(row)}>
          {t('admin.tenant.edit.action')}
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('admin.tenant.title')}
        description={t('admin.tenant.description')}
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('admin.tenant.newTenant')}
          </Button>
        }
      />

      {mfaTicket ? (
        <AlertBanner variant="warning" title={t('admin.grant.mfaTicket.title')} onDismiss={() => setMfaTicket(null)}>
          <p>
            {t('admin.tenant.create.mfaTicket.body', {
              username: mfaTicket.username,
              token: mfaTicket.token,
              expiresAt: formatInstantDisplay(mfaTicket.expiresAt, locale),
            })}
          </p>
        </AlertBanner>
      ) : null}

      <DataTable
        caption={t('admin.tenant.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        emptyTitle={t('admin.tenant.empty.title')}
        emptyBody={t('admin.tenant.empty.body')}
      />

      <OnboardTenantModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={(result) => {
          invalidate();
          push({ variant: 'success', title: t('admin.tenant.toast.onboarded', { name: result.tenant.nameEn }) });
          if (result.mfaEnrolment) {
            setMfaTicket({ token: result.mfaEnrolment.token, expiresAt: result.mfaEnrolment.expiresAt, username: result.secretary.username });
          }
        }}
      />
      <EditTenantModal
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        tenant={editTarget}
        onSuccess={(updated) => {
          invalidate();
          push({ variant: 'success', title: t('admin.tenant.toast.updated', { name: updated.nameEn }) });
        }}
      />
    </div>
  );
}
